import React, { useMemo, useState, useEffect } from 'react';
import { apiClient } from '../../services/apiClient';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

interface TenantSubscription {
  tier: string;
  plan: string;
  status: string;
  price: number;
  billing_period: string;
  current_period_end: string | null;
}

interface Tenant {
  id: string;
  name: string;
  domain: string;
  is_active: boolean;
  is_system?: boolean;
  created_at: string;
  users_count: number;
  subscription: TenantSubscription | null;
}

const TenantsManagement: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.get('/admin/tenants/');
      setTenants(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  const filteredTenants = tenants.filter(
    (t) =>
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.domain && t.domain.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const tierBadgeColor = (tier: string) => {
    switch (tier?.toLowerCase()) {
      case 'growth':
        return 'bg-purple-100 text-purple-800';
      case 'pro':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleRemoveTenant = async (tenant: Tenant) => {
    if (tenant.is_system) return;
    setConfirmRemove({ id: tenant.id, name: tenant.name });
  };

  const confirmRemoveTenant = async () => {
    if (!confirmRemove) return;
    try {
      setRemovingId(confirmRemove.id);
      await apiClient.delete(`/admin/tenants/${confirmRemove.id}/`);
      setTenants((prev) => prev.filter((t) => t.id !== confirmRemove.id));
      setConfirmRemove(null);
    } catch (err: any) {
      setError(err.message || 'Failed to remove tenant');
    } finally {
      setRemovingId(null);
    }
  };

  const statusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trial':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'past_due':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = useMemo<Column<Tenant>[]>(
    () => [
      {
        id: 'tenant',
        header: 'Tenant',
        mobilePriority: 'primary',
        mobileLabel: 'Tenant',
        render: (tenant) => (
          <div>
            <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
            {tenant.domain && <div className="text-xs text-gray-500">{tenant.domain}</div>}
          </div>
        ),
      },
      {
        id: 'users',
        header: 'Users',
        mobileLabel: 'Users',
        render: (tenant) => <span className="text-sm text-gray-600">{tenant.users_count}</span>,
      },
      {
        id: 'tier',
        header: 'Tier',
        mobileLabel: 'Tier',
        render: (tenant) =>
          tenant.subscription ? (
            <span
              className={`px-2 py-1 text-xs font-semibold rounded ${tierBadgeColor(tenant.subscription.tier)}`}
            >
              {tenant.subscription.tier?.toUpperCase() || '-'}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (tenant) =>
          tenant.subscription ? (
            <span
              className={`px-2 py-1 text-xs font-semibold rounded ${statusBadgeColor(tenant.subscription.status)}`}
            >
              {tenant.subscription.status}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      {
        id: 'price',
        header: 'Price',
        mobileLabel: 'Price',
        render: (tenant) => (
          <span className="text-sm text-gray-600">
            {tenant.subscription
              ? `$${tenant.subscription.price}/${tenant.subscription.billing_period === 'monthly' ? 'mo' : 'yr'}`
              : '-'}
          </span>
        ),
      },
      {
        id: 'period_end',
        header: 'Period End',
        mobileLabel: 'Period End',
        render: (tenant) => (
          <span className="text-sm text-gray-600">
            {formatDate(tenant.subscription?.current_period_end || '')}
          </span>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        mobileLabel: 'Created',
        render: (tenant) => (
          <span className="text-sm text-gray-600">{formatDate(tenant.created_at)}</span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        mobileLabel: 'Actions',
        className: 'text-right',
        render: (tenant) =>
          !tenant.is_system ? (
            <div data-row-action className="text-right">
              <button
                onClick={() => handleRemoveTenant(tenant)}
                disabled={!!removingId}
                className="text-red-600 hover:text-red-800 disabled:opacity-50 text-sm"
                title="Remove tenant"
              >
                {removingId === tenant.id ? 'Removing...' : 'Remove'}
              </button>
            </div>
          ) : null,
      },
    ],
    [removingId]
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Tenants</h2>
            <p className="text-sm text-gray-500 mt-1">
              View all tenants and their subscription status
            </p>
          </div>
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-indigo-500"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <DataListTable<Tenant>
        columns={columns}
        rows={filteredTenants}
        getRowKey={(tenant) => tenant.id}
        loading={loading}
        emptyContent={
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 py-12 text-center text-gray-500">
            No tenants found.
          </div>
        }
      />

      {confirmRemove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove tenant?</h3>
            <p className="text-gray-600 mb-4">
              This will permanently remove <strong>{confirmRemove.name}</strong> and all associated
              users, data, and subscriptions. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmRemove(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveTenant}
                disabled={!!removingId}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {removingId ? 'Removing...' : 'Remove tenant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantsManagement;
